import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import api, { normalize } from '../../services/api';
import './NfseConsulta.css';

function money(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function NfseAjustePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useRef(null);
  const refIdParam = searchParams.get('refId');

  const [autorizadas, setAutorizadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  const [form, setForm] = useState({
    nfseReferenciaAjusteId: refIdParam ? Number(refIdParam) : null,
    motivoAjuste: '',
    valorServico: null,
    valorAjusteBc: 0,
    aliquotaIbs: 0,
    aliquotaCbs: 0,
    aliquotaIss: 0,
    issRetido: false,
    codigoItemServico: '',
    discriminacao: '',
    cstIbs: '',
    cClassTribIbs: '',
    cstCbs: '',
    cClassTribCbs: '',
    tomadorAgenteId: null,
  });

  const setCampo = (c, v) => setForm((p) => ({ ...p, [c]: v }));

  const carregarAutorizadas = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/nfse/emitidas', { params: { status: 'Autorizada' } });
      setAutorizadas(normalize(data));
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: e.response?.data?.message || 'Falha ao carregar NFS-e autorizadas.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarAutorizadas();
  }, [carregarAutorizadas]);

  const aplicarReferencia = async (id) => {
    if (!id) return;
    setCampo('nfseReferenciaAjusteId', id);
    try {
      const { data } = await api.get(`/nfse/emitidas/${id}`);
      const ref = normalize(data);
      setForm((p) => ({
        ...p,
        nfseReferenciaAjusteId: ref.id,
        tomadorAgenteId: ref.tomadorAgenteId ?? null,
        valorServico: ref.valores?.valorServico ?? ref.valorServicos ?? 0,
        aliquotaIss: ref.aliquotaIss ?? 0,
        aliquotaIbs: ref.aliquotaIbs ?? 0,
        aliquotaCbs: ref.aliquotaCbs ?? 0,
        codigoItemServico: ref.codigoItemServico || '',
        discriminacao: ref.discriminacao || `Ajuste referente à NFS-e ${ref.numeroNfse || ref.id}`,
        issRetido: !!ref.issRetido,
        cstIbs: ref.cstIbs || '',
        cClassTribIbs: ref.cClassTribIbs || '',
        cstCbs: ref.cstCbs || '',
        cClassTribCbs: ref.cClassTribCbs || '',
        valorAjusteBc: ref.valores?.valorAjusteBc ?? 0,
      }));
    } catch (e) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Referência',
        detail: e.response?.data?.message || 'Não foi possível carregar a nota de referência.',
      });
    }
  };

  useEffect(() => {
    if (refIdParam) aplicarReferencia(Number(refIdParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refIdParam]);

  const refOptions = useMemo(
    () =>
      autorizadas.map((n) => ({
        label: `${n.numeroNfse || `ID ${n.id}`} — ${n.tomadorNome || 'Sem tomador'} — ${money(n.valorServicos)}`,
        value: n.id,
      })),
    [autorizadas]
  );

  const requestCalculo = async () => {
    if (form.valorServico == null) {
      setPreview(null);
      return;
    }
    setCalcLoading(true);
    try {
      const { data } = await api.post('/nfse/emitidas/calculo', {
        valorServico: Number(form.valorServico) || 0,
        aliquotaIss: Number(form.aliquotaIss) || 0,
        issRetido: !!form.issRetido,
        aliquotaIbs: Number(form.aliquotaIbs) || 0,
        aliquotaCbs: Number(form.aliquotaCbs) || 0,
        valorAjusteBc: Number(form.valorAjusteBc) || 0,
      });
      setPreview(normalize(data));
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Cálculo',
        detail: e.response?.data?.message || 'Falha no preview.',
      });
    } finally {
      setCalcLoading(false);
    }
  };

  const emitir = async (emitirAgora) => {
    if (!form.nfseReferenciaAjusteId) {
      toast.current?.show({ severity: 'warn', summary: 'Referência', detail: 'Selecione a NFS-e autorizada de referência.' });
      return;
    }
    if (!form.motivoAjuste?.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Motivo', detail: 'Informe o motivo do ajuste.' });
      return;
    }
    if (form.valorServico == null || Number(form.valorServico) < 0) {
      toast.current?.show({ severity: 'warn', summary: 'Valor', detail: 'Informe o valor do serviço.' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/nfse/emitidas', {
        tipoDocumento: 'Ajuste',
        motivoAjuste: form.motivoAjuste,
        nfseReferenciaAjusteId: form.nfseReferenciaAjusteId,
        tomadorAgenteId: form.tomadorAgenteId,
        valorServico: Number(form.valorServico) || 0,
        valorAjusteBc: Number(form.valorAjusteBc) || 0,
        aliquotaIbs: Number(form.aliquotaIbs) || 0,
        aliquotaCbs: Number(form.aliquotaCbs) || 0,
        aliquotaIss: Number(form.aliquotaIss) || 0,
        issRetido: !!form.issRetido,
        codigoItemServico: form.codigoItemServico || null,
        discriminacao: form.discriminacao || null,
        cstIbs: form.cstIbs || null,
        cClassTribIbs: form.cClassTribIbs || null,
        cstCbs: form.cstCbs || null,
        cClassTribCbs: form.cClassTribCbs || null,
        origem: 'Manual',
        emitirAgora: !!emitirAgora,
      });
      toast.current?.show({
        severity: 'success',
        summary: emitirAgora ? 'Emissão' : 'Rascunho',
        detail: emitirAgora ? 'Nota de ajuste enviada.' : 'Rascunho de ajuste salvo.',
      });
      navigate('/nfse/emissor');
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: e.response?.data?.message || 'Falha ao criar nota de ajuste.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nfse-emissao-page">
      <Toast ref={toast} />

      <div className="nfse-emissao-header">
        <h1>Nota de ajuste IBS/CBS</h1>
        <Button
          label="Voltar"
          icon="pi pi-arrow-left"
          className="p-button-outlined"
          onClick={() => navigate('/nfse/emissor')}
        />
      </div>

      <div className="nfse-emissao-card">
        <div className="nfse-emissao-ajuste-banner">
          Emite NFS-e com tipoDocumento = Ajuste, vinculada a uma nota autorizada (vAjusteBC / IBS / CBS).
        </div>

        <div className="nfse-emissao-grid">
          <div className="nfse-emissao-field nfse-emissao-field-full">
            <label>NFS-e autorizada de referência *</label>
            <Dropdown
              value={form.nfseReferenciaAjusteId}
              options={refOptions}
              onChange={(e) => aplicarReferencia(e.value)}
              placeholder={loading ? 'Carregando...' : 'Selecione'}
              filter
              filterPlaceholder="Buscar..."
              emptyMessage="Nenhuma autorizada"
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field nfse-emissao-field-full">
            <label>Motivo do ajuste *</label>
            <InputTextarea
              value={form.motivoAjuste}
              onChange={(e) => setCampo('motivoAjuste', e.target.value)}
              rows={3}
              autoResize
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field">
            <label>Valor do serviço</label>
            <InputNumber
              value={form.valorServico}
              onValueChange={(e) => setCampo('valorServico', e.value)}
              onBlur={requestCalculo}
              mode="currency"
              currency="BRL"
              locale="pt-BR"
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field">
            <label>Valor ajuste BC (vAjusteBC)</label>
            <InputNumber
              value={form.valorAjusteBc}
              onValueChange={(e) => setCampo('valorAjusteBc', e.value ?? 0)}
              onBlur={requestCalculo}
              mode="currency"
              currency="BRL"
              locale="pt-BR"
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field">
            <label>Alíquota IBS (%)</label>
            <InputNumber
              value={form.aliquotaIbs}
              onValueChange={(e) => setCampo('aliquotaIbs', e.value ?? 0)}
              onBlur={requestCalculo}
              minFractionDigits={2}
              maxFractionDigits={4}
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field">
            <label>Alíquota CBS (%)</label>
            <InputNumber
              value={form.aliquotaCbs}
              onValueChange={(e) => setCampo('aliquotaCbs', e.value ?? 0)}
              onBlur={requestCalculo}
              minFractionDigits={2}
              maxFractionDigits={4}
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field">
            <label>CST IBS</label>
            <InputText value={form.cstIbs} onChange={(e) => setCampo('cstIbs', e.target.value)} className="w-full" />
          </div>
          <div className="nfse-emissao-field">
            <label>cClassTrib IBS</label>
            <InputText value={form.cClassTribIbs} onChange={(e) => setCampo('cClassTribIbs', e.target.value)} className="w-full" />
          </div>
          <div className="nfse-emissao-field">
            <label>CST CBS</label>
            <InputText value={form.cstCbs} onChange={(e) => setCampo('cstCbs', e.target.value)} className="w-full" />
          </div>
          <div className="nfse-emissao-field">
            <label>cClassTrib CBS</label>
            <InputText value={form.cClassTribCbs} onChange={(e) => setCampo('cClassTribCbs', e.target.value)} className="w-full" />
          </div>
          <div className="nfse-emissao-field nfse-emissao-field-full">
            <label>Discriminação</label>
            <InputTextarea
              value={form.discriminacao}
              onChange={(e) => setCampo('discriminacao', e.target.value)}
              rows={3}
              autoResize
              className="w-full"
            />
          </div>
        </div>

        <div className="nfse-emissao-preview">
          <div className="nfse-emissao-preview-head">
            <h3>Preview do cálculo</h3>
            <Button
              label="Recalcular"
              icon="pi pi-refresh"
              className="p-button-text p-button-sm"
              loading={calcLoading}
              onClick={requestCalculo}
            />
          </div>
          {preview ? (
            <ul>
              <li>Base: {money(preview.baseCalculo)}</li>
              <li>IBS: {money(preview.valorIbs)} · CBS: {money(preview.valorCbs)}</li>
              <li>Ajuste BC: {money(preview.valorAjusteBc)}</li>
              <li>Líquido: {money(preview.valorLiquido)}</li>
            </ul>
          ) : (
            <p>Ajuste os valores e recalcule.</p>
          )}
        </div>

        <div className="nfse-emissao-actions">
          <Button label="Cancelar" className="p-button-text" onClick={() => navigate('/nfse/emissor')} />
          <Button
            label="Salvar rascunho"
            icon="pi pi-save"
            className="p-button-outlined"
            loading={saving}
            onClick={() => emitir(false)}
          />
          <Button label="Emitir ajuste" icon="pi pi-send" loading={saving} onClick={() => emitir(true)} />
        </div>
      </div>
    </div>
  );
}
